import { TableBody, TableRow, TableCell } from "../ui/table";
import { Checkbox } from "../ui/checkbox";
import CurrentActions from "./current-actions";
import type {
  TableColumn,
  TableAction,
  ValidTableNames,
  TableItemType,
} from "@/hooks/table/types";
import { useColumnWidths } from "@/hooks/useColumnWidths";
import { cn } from "@/lib/utils";
import type { CSSProperties } from "react";

interface DataTableBodyProps<T extends ValidTableNames = any> {
  sortedData: TableItemType<T>[];
  selectedRows: Set<string | number>;
  enableRowSelection?: boolean;
  displayColumns: TableColumn<T>[];
  handleRowSelection: (rowId: string | number) => void;
  getRowId: (row: TableItemType<T>) => string | number;
  rowActions?: (row: TableItemType<T>) => TableAction<T>[];
  tableId?: string;
  /** Estilos sticky por key para las columnas pinneadas. */
  pinnedStyles?: Record<string, CSSProperties>;
  /** Fija la columna de selección (checkbox) a la izquierda (hay pins). */
  selectionSticky?: boolean;
  /** Clase CSS extra por fila (filas especiales, p. ej. borradores locales). */
  rowClassName?: (row: TableItemType<T>) => string | undefined;
  /** Si devuelve false, la fila no muestra checkbox (queda fuera del bulk). */
  isRowSelectable?: (row: TableItemType<T>) => boolean;
}

export default function DataTableBody<T extends ValidTableNames = any>({
  sortedData,
  selectedRows,
  enableRowSelection,
  displayColumns,
  handleRowSelection,
  getRowId,
  rowActions,
  tableId,
  pinnedStyles,
  selectionSticky,
  rowClassName,
  isRowSelectable,
}: DataTableBodyProps<T>) {
  const { getWidth } = useColumnWidths(tableId);
  return (
    <TableBody>
      {sortedData.map((row, index) => {
        const rowId = getRowId(row);
        const isSelected = selectedRows.has(rowId);
        const currentActions = rowActions ? rowActions(row) : undefined;
        const selectable = isRowSelectable ? isRowSelectable(row) : true;

        return (
          <TableRow
            key={row._id || index}
            className={cn(
              "group",
              isSelected && "bg-zinc-200 dark:bg-zinc-900",
              rowClassName?.(row),
            )}
          >
            {enableRowSelection && (
              <TableCell
                className={cn(
                  "w-9! h-8! p-0! [&>[role=checkbox]]:translate-y-0!",
                  // Sticky (izquierda): fondo solido para tapar celdas detras durante
                  // scroll horizontal. En filas seleccionadas se vuelve transparente
                  // para dejar ver el bg de la fila.
                  selectionSticky &&
                    cn(
                      "sticky left-0 z-[11] group-hover:bg-[color-mix(in_srgb,var(--muted)_50%,var(--card))]",
                      isSelected
                        ? "bg-zinc-200 dark:bg-zinc-900"
                        : "bg-card",
                    ),
                )}
              >
                <div className="flex items-center justify-start h-full w-full">
                  {selectable && (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleRowSelection(rowId)}
                      aria-label={`Seleccionar fila ${index + 1}`}
                    />
                  )}
                </div>
              </TableCell>
            )}
            {displayColumns.map((column) => {
              const width = getWidth(column.key);
              // Ancho explícito (columna redimensionada), expuesto como --col-w
              // para que el texto truncado use todo el ancho de la columna
              // (regla `td[data-col-sized] .truncate` en index.css). Sin
              // max-width: el contenido no truncable (badges de moneda, fechas)
              // ensancha la columna en vez de encimarse sobre la siguiente.
              const style: React.CSSProperties | undefined =
                typeof width === "number"
                  ? ({
                      width: `${width}px`,
                      minWidth: `${width}px`,
                      "--col-w": `${width}px`,
                    } as React.CSSProperties)
                  : undefined;
              const pinStyle = pinnedStyles?.[column.key as string];
              const pinned = !!pinStyle;
              return (
                <TableCell
                  key={column.key as string}
                  data-column-key={column.key as string}
                  data-col-sized={typeof width === "number" ? "" : undefined}
                  className={cn(
                    column.className,
                    // Celda pinneada: fondo sólido (sticky) que sigue el hover.
                    // z-15 la pone sobre las celdas normales (z-auto) y bajo el
                    // header (z-20) y pinneadas del header (z-30).
                    // Si la fila está seleccionada, el fondo se vuelve transparente
                    // para que se vea el bg de la fila.
                    pinned &&
                      cn(
                        "z-15 group-hover:bg-[color-mix(in_srgb,var(--muted)_50%,var(--card))]",
                        isSelected
                          ? "bg-zinc-200 dark:bg-zinc-900"
                          : "bg-card",
                      ),
                  )}
                  style={pinStyle ? { ...style, ...pinStyle } : style}
                >
                  <div className="space-y-1 text-xs">
                    {column.render
                      ? column.render(
                          row[column.key as keyof TableItemType<T>],
                          row,
                        )
                      : (row[column.key as keyof TableItemType<T>] as any)}
                  </div>
                </TableCell>
              );
            })}
            {currentActions && (
              <TableCell
                className={cn(
                  "w-8! min-w-8! max-w-8! text-center sticky right-0 z-10 p-0 group-hover:bg-[color-mix(in_srgb,var(--muted)_50%,var(--card))]",
                  isSelected ? "bg-zinc-200 dark:bg-zinc-900" : "bg-card",
                )}
              >
                <CurrentActions
                  row={row as any}
                  actions={currentActions as any}
                />
              </TableCell>
            )}
          </TableRow>
        );
      })}
    </TableBody>
  );
}
